import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { AlertTriangle, ShieldCheck, ChevronLeft, ArrowUpRight, BadgeCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import { CornerAccents } from '@/components/CornerAccents';
import { Reveal } from '@/components/motion/Reveal';
import { SpotlightCard } from '@/components/motion/SpotlightCard';

const LC_URL = 'https://getlcshield.com';
const LC_ICON = '/press-kit/lcshield-logo.png';
const LC_PREVIEW = '/press-kit/lcshield-og.png';
const HEADING_FONT = { fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" };
const BODY_FONT = { fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" };

const content = {
  'zh-TW': {
    badge: 'B2B 金融科技／AI SaaS',
    name: 'LC Shield',
    tagline: 'AI 驅動的 UCP 600 與 MT700 單據瑕疵審核助手 — 通常 30 秒內完成信用證單據合規核對。',
    visit: '啟動應用',
    badges: ['AI 單據審核', '依 UCP 600 核對', '私隱優先', '英文介面'],
    challenge_label: '挑戰',
    challenge: '銀行會對遭拒付的交單收取瑕疵費用，而拒付亦可能令付款延誤，直至瑕疵獲解決 — 根源是 ICC 規則下的人手單據核對疏漏。',
    solution_label: '解決方案',
    solution: '我們打造了全端、端到端的 SaaS：依 UCP 600 與 ISBP 821 (2023) 規則交叉核對貿易單據（通常 30 秒內），內建互動沙盒、ROI 計算器、SWIFT MT700 自動對映，以及原生 PDF／CSV 報告匯出。',
    g_label: '保證與政策',
    g_text: '如 LC Shield 遺漏了您的銀行在拒付通知中引述的瑕疵，而您在拒付後 30 天內把該通知發送給我們，我們將退還您為該次交單所支付的審核費用。免費審核與示例報告不涉及審核費用，不在退款範圍內。',
    ill: '示意範例',
    cta_title: '交單之前，先讓 AI 審一次',
    cta_desc: '先核對，後交單 — 付費審核由我們的審核費保證（Audit Fee Guarantee）支持。',
    back: '產品展示',
  },
  'zh-CN': {
    badge: 'B2B 金融科技／AI SaaS',
    name: 'LC Shield',
    tagline: 'AI 驱动的 UCP 600 与 MT700 单据瑕疵审核助手 — 通常 30 秒内完成信用证单据合规核对。',
    visit: '启动应用',
    badges: ['AI 单据审核', '依 UCP 600 核对', '隐私优先', '英文界面'],
    challenge_label: '挑战',
    challenge: '银行会对遭拒付的交单收取瑕疵费用，而拒付亦可能令付款延误，直至瑕疵获解决 — 根源是 ICC 规则下的人手单据核对疏漏。',
    solution_label: '解决方案',
    solution: '我们打造了全栈、端到端的 SaaS：依 UCP 600 与 ISBP 821 (2023) 规则交叉核对贸易单据（通常 30 秒内），内建交互沙盒、ROI 计算器、SWIFT MT700 自动映射，以及原生 PDF／CSV 报告导出。',
    g_label: '保证与政策',
    g_text: '如 LC Shield 遗漏了您的银行在拒付通知中引述的瑕疵，而您在拒付后 30 天内把该通知发送给我们，我们将退还您为该次交单所支付的审核费用。免费审核与示例报告不涉及审核费用，不在退款范围内。',
    ill: '示意示例',
    cta_title: '交单之前，先让 AI 审一次',
    cta_desc: '先核对，后交单 — 付费审核由我们的审核费保证（Audit Fee Guarantee）支持。',
    back: '产品展示',
  },
  en: {
    badge: 'B2B FinTech / AI SaaS',
    name: 'LC Shield',
    tagline: 'AI-Powered UCP 600 & MT700 Discrepancy Assistant — letter of credit document compliance checks, typically under 30 seconds.',
    visit: 'Launch App',
    badges: ['AI Document Audit', 'Checks against UCP 600', 'Privacy-First', 'English interface'],
    challenge_label: 'The Challenge',
    challenge: 'Banks charge discrepancy fees on refused presentations, and a refusal can delay payment while the discrepancy is resolved — caused by manual document checking gaps under ICC rules.',
    solution_label: 'The Solution',
    solution: 'We built a full-stack, end-to-end SaaS that cross-checks trade documents against UCP 600 & ISBP 821 (2023) rules, typically under 30 seconds — featuring interactive sandboxes, ROI calculators, SWIFT MT700 auto-mapping, and native PDF/CSV report generation.',
    g_label: 'Guarantee & Policies',
    g_text: "If LC Shield misses a discrepancy that your bank cites in a refusal notice, and you send us that notice within 30 days of the refusal, we refund the audit fee you paid for that presentation. Free audits and sample reports have no audit fee and are not covered by the refund.",
    ill: 'Illustrative example',
    cta_title: 'Let AI audit before you present',
    cta_desc: 'Check before you present. Paid audits are backed by our Audit Fee Guarantee.',
    back: 'Products',
  },
};

const TERMINAL_LINES = [
  { text: '$ lcshield audit LC_MT700_4471.txt', color: '#94A3B8' },
  { text: '✓ UCP 600 Art.14 — compliant', color: '#10B981' },
  { text: '✗ B/L date > 21d after shipment (Art.14c)', color: '#EF4444' },
  { text: '⚠ invoice description mismatch — field 45A', color: '#F59E0B' },
  { text: '✓ 3 documents checked · typically under 30 seconds', color: '#10B981' },
];

function StatusPill() {
  return (
    <span data-testid="lc-status-pill" className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 font-mono text-[10px] font-bold tracking-[0.2em] text-[#10B981]">
      <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" style={{ animation: 'pulse-dot 2s ease-in-out infinite' }} />
      LC SHIELD: LIVE &amp; OPERATIONAL
    </span>
  );
}

function VisitButton({ label, large = false }) {
  return (
    <a href={LC_URL} target="_blank" rel="noopener noreferrer">
      <Button
        data-testid={large ? 'lc-visit-btn-bottom' : 'lc-visit-btn'}
        className={`${large ? 'h-14 px-10 text-base' : 'h-12 px-8 text-sm'} rounded-full bg-[#F59E0B] text-[#080B10] font-bold hover:brightness-110 transition-all shadow-[0_8px_28px_rgba(245,158,11,0.35)]`}
      >
        {label}
        <ArrowUpRight className="ml-2 h-4 w-4" />
      </Button>
    </a>
  );
}

function HeroPreview() {
  return (
    <SpotlightCard glow="#F59E0B" className="rounded-2xl">
      <div className="relative rounded-2xl overflow-hidden border border-slate-800/60 bg-[#0E131F] shadow-[0_24px_60px_rgba(0,0,0,0.5)]">
        <CornerAccents color="#F59E0B" />
        <div className="h-8 flex items-center gap-1.5 px-4 bg-[#0E131F] border-b border-slate-800/60">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-[#64748B]">getlcshield.com</span>
        </div>
        <img src={LC_PREVIEW} alt="LC Shield product preview" className="w-full" data-testid="lc-preview-image" />
        <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-2">
          {['UCP 600', 'ISBP 821 (2023)', 'MT700'].map((tag) => (
            <span key={tag} className="px-2 py-1 rounded font-mono text-[9px] font-bold tracking-wider text-[#818CF8] bg-[#080B10]/80 backdrop-blur border border-[#6366F1]/40">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </SpotlightCard>
  );
}

function Terminal() {
  return (
    <SpotlightCard glow="#F59E0B" className="rounded-2xl max-w-3xl mx-auto">
      <div data-testid="lc-terminal" className="relative rounded-2xl bg-[#0E131F] border border-slate-800/60 overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.4)]">
        <CornerAccents color="#F59E0B" />
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800/60 bg-white/[0.02]">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          </div>
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#64748B]">LCSHIELD / AUDIT_ENGINE</span>
        </div>
        <div className="p-6 md:p-8 font-mono text-xs md:text-sm space-y-2.5">
          {TERMINAL_LINES.map((line, i) => (
            <motion.p
              key={line.text}
              initial={{ opacity: 0, x: 12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 + i * 0.35, duration: 0.4 }}
              style={{ color: line.color }}
            >
              {line.text}
            </motion.p>
          ))}
          <span className="inline-block w-2 h-4 bg-[#F59E0B] align-middle" style={{ animation: 'blip 1s step-end infinite' }} />
        </div>
      </div>
    </SpotlightCard>
  );
}

export default function LCShieldPage() {
  const { lang } = useLanguage();
  const c = content[lang] || content.en;

  return (
    <div data-testid="lcshield-page" className="bg-[#080B10] text-white min-h-screen">
      <SEOHead path="/products/lc-shield" />

      <section className="relative min-h-[92vh] flex items-center overflow-hidden pt-24 pb-16">
        <div className="absolute inset-0">
          <img src="/img/hero-architecture.jpg" alt="" aria-hidden className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#080B10]/[0.9]" />
          <div
            className="absolute inset-0"
            style={{ background: 'radial-gradient(640px circle at 15% 25%, rgba(245,158,11,0.10), transparent 55%), radial-gradient(560px circle at 85% 75%, rgba(99,102,241,0.12), transparent 55%)' }}
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            className="lg:col-span-6"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] }}
          >
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <StatusPill />
              <span className="px-3 py-1.5 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-xs font-bold text-[#F59E0B] tracking-wide">{c.badge}</span>
            </div>
            <div className="flex items-center gap-5 mb-6">
              <img
                src={LC_ICON}
                alt="LC Shield glowing shield emblem"
                data-testid="lc-detail-icon"
                className="w-16 md:w-20 h-auto"
                style={{ filter: 'drop-shadow(0 0 14px rgba(56,189,248,0.65)) drop-shadow(0 0 30px rgba(56,189,248,0.3))' }}
              />
              <div>
                <h1
                  data-testid="lc-detail-title"
                  className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-bold text-[#F8FAFC]"
                  style={{ fontFamily: "Georgia, 'Times New Roman', 'Noto Serif TC', serif", textShadow: '0 0 24px rgba(56,189,248,0.35)' }}
                >
                  {c.name}
                </h1>
                <p className="text-[#64748B] font-mono text-xs mt-1 tracking-widest">getlcshield.com</p>
              </div>
            </div>
            <p data-testid="lc-detail-tagline" className="text-base md:text-lg text-[#94A3B8] max-w-lg leading-relaxed mb-6" style={BODY_FONT}>
              {c.tagline}
            </p>
            <div className="flex flex-wrap gap-2 mb-8" data-testid="lc-badges">
              {c.badges.map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-slate-800/60 text-xs font-medium text-[#94A3B8] hover:-translate-y-0.5 hover:border-[#F59E0B]/40 transition-all duration-200">
                  <BadgeCheck className="h-3.5 w-3.5 text-[#F59E0B]" />
                  {b}
                </span>
              ))}
            </div>
            <VisitButton label={c.visit} />
          </motion.div>
          <motion.div
            className="lg:col-span-6 hidden lg:block"
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.25, ease: [0.21, 0.47, 0.32, 0.98] }}
          >
            <HeroPreview />
          </motion.div>
        </div>
      </section>

      <section data-testid="lc-challenge-section" className="py-24 md:py-28 border-t border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="h-5 w-5 text-[#F59E0B]" />
              <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#F59E0B]">{c.challenge_label}</p>
            </div>
            <p data-testid="lc-challenge-text" className="text-xl md:text-2xl tracking-tight font-semibold text-[#F8FAFC]/85 max-w-3xl leading-relaxed" style={HEADING_FONT}>
              {c.challenge}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Terminal />
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-widest text-[#64748B]">{c.ill}</p>
          </Reveal>
        </div>
      </section>

      <section data-testid="lc-solution-section" className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="h-5 w-5 text-[#818CF8]" />
              <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#818CF8]">{c.solution_label}</p>
            </div>
            <p data-testid="lc-solution-text" className="text-xl md:text-2xl tracking-tight font-semibold text-[#F8FAFC]/85 max-w-3xl leading-relaxed" style={HEADING_FONT}>
              {c.solution}
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="lc-guarantee-section" className="py-24 md:py-28 border-t border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <BadgeCheck className="h-5 w-5 text-[#F59E0B]" />
              <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#F59E0B]">{c.g_label}</p>
            </div>
            <p data-testid="lc-guarantee-text" className="text-xl md:text-2xl tracking-tight font-semibold text-[#F8FAFC]/85 max-w-3xl leading-relaxed mb-8" style={HEADING_FONT}>
              {c.g_text}
            </p>
            <div className="flex flex-wrap gap-3" data-testid="lc-policy-links">
              {[
                ['Audit Fee Guarantee', 'https://getlcshield.com/guarantee'],
                ['Security', 'https://getlcshield.com/security'],
                ['Terms', 'https://getlcshield.com/terms'],
                ['Privacy', 'https://getlcshield.com/privacy'],
              ].map(([label, href]) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`lc-policy-link-${label.split(' ')[0].toLowerCase()}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/[0.04] border border-slate-800/60 text-sm text-[#94A3B8] hover:text-white hover:border-[#F59E0B]/40 transition-all"
                >
                  {label}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="lc-cta-section" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="relative rounded-3xl bg-[#0E131F] border border-slate-800/60 px-8 py-14 md:px-16 md:py-20 text-center overflow-hidden">
              <CornerAccents color="#F59E0B" />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: 'radial-gradient(480px circle at 25% 30%, rgba(245,158,11,0.10), transparent 60%), radial-gradient(480px circle at 75% 70%, rgba(99,102,241,0.10), transparent 60%)' }}
              />
              <div className="relative z-10">
                <img
                  src={LC_ICON}
                  alt="LC Shield"
                  className="w-14 h-auto mx-auto mb-6"
                  style={{ filter: 'drop-shadow(0 0 12px rgba(56,189,248,0.6)) drop-shadow(0 0 26px rgba(56,189,248,0.3))' }}
                />
                <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#F8FAFC] mb-4" style={HEADING_FONT}>{c.cta_title}</h2>
                <p className="text-base md:text-lg text-[#94A3B8] mb-8" style={BODY_FONT}>{c.cta_desc}</p>
                <div className="flex justify-center"><VisitButton label={c.visit} large /></div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <Link to="/products" data-testid="lc-back-to-products" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-white transition-colors">
          <ChevronLeft className="h-4 w-4" />
          {c.back}
        </Link>
      </div>
    </div>
  );
}
